import { isNil } from 'ramda';
import videojs, { VideoJsPlayer, VideoJsPlayerOptions } from 'video.js';

import { Event } from '@src/types/event';
import { attachPluginToPlayer } from '@src/utils/plugin';

const Plugin = videojs.getPlugin('plugin');

export const PLUGIN_NAME = 'uiManagerPlugin';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface Options {}

class UIManagerPlugin extends Plugin {
  vjsPlayer: VideoJsPlayer;
  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);

    this.vjsPlayer = player;

    // make sure to add listener AFTER the one in video.js, so to override the gesture handling
    // https://github.dev/videojs/video.js/blob/4e2f8add411d38549a39cf4e52172d9a01158da0/src/js/player.js#L582-L583
    attachPluginToPlayer(this, player);

    this.vjsPlayer.one(Event.PLAY, this.handleMouseLeave);
    this.vjsPlayer.on(Event.ENDED, this.handleEnded);
    this.vjsPlayer.on(Event.FULLSCREENCHANGE, this.handleFullscreenChange);
    this.vjsPlayer.on(
      Event.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );
    this.handleVolumeControlGesture();

    this.vjsPlayer.on(Event.PLAYERRESIZE, this.handlePlayerResize);
    this.handlePlayerResize();

    this.setupHandleInIframe();
    this.setupHandleFullscreenToggle();
  }

  handleMouseLeave = () => {
    // aim to dismiss control bar immediately
    this.vjsPlayer.on(
      Event.MOUSELEAVE,
      (/* evt: videojs.EventTarget.Event */) => {
        this.vjsPlayer?.userActive?.(false);
      }
    );
  };

  handleEnded = () => {
    const isFullscreen = this.player.isFullscreen();
    const isPaused = this.player.paused();

    const currentTime = this.player.currentTime();
    const duration = this.player.duration();
    const threshold = 2; // threshold to tolerate in seconds

    const isCurrentTimeAtEnd =
      currentTime > 0 && duration > 0 && currentTime >= duration - threshold;

    // TODO not to exitfullscreen if auto-next toggle is being turned on
    if (isFullscreen && isPaused && isCurrentTimeAtEnd) {
      this.player.exitFullscreen();
    }
  };

  // handle auto exit fullscreen when midroll or postroll end, and video ended event not fired
  handleContentResumeRequested = () => {
    const duration = Math.floor(this.player.duration());
    const currentTime = Math.floor(this.player.currentTime());
    const isFullscreen = this.player.isFullscreen();

    if (isFullscreen && currentTime > 0 && currentTime === duration) {
      this.player.exitFullscreen();
    }
  };

  handleFullscreenChange = () => {
    this.vjsPlayer.controlBar?.qualityLevelMenuButton?.unpressButton?.();
    this.vjsPlayer.controlBar?.subsCapsButton?.unpressButton?.();
  };

  private handleVolumeControlGesture() {
    // TODO may also detect if control bar(with volume slider)'s width is longer than player width
    const shouldDisableShowVolumeControlSlider =
      this.player.hasClass('vjs-touch-enabled');

    if (shouldDisableShowVolumeControlSlider) {
      this.player.addClass('vjs-volume-control-hide-slider');
    }
  }

  handlePlayerResize = () => {
    const width = this.vjsPlayer.currentWidth();
    const height = this.vjsPlayer.currentHeight();
    const el = this.vjsPlayer.el() as HTMLDivElement;

    if (el?.style) {
      if (!isNil(width)) {
        el.style.setProperty('--player-width', `${width}px`);
      }
      if (!isNil(height)) {
        el.style.setProperty('--player-height', `${height}px`);
      }
    }
  };

  setupHandleInIframe() {
    if (this.vjsPlayer.options_.inIframe) {
      this.vjsPlayer.addClass('vjs-in-iframe');
    }
  }

  setupHandleFullscreenToggle() {
    if (this.vjsPlayer.options_.hideFullscreenToggle === 'inlineonly') {
      this.vjsPlayer.addClass('vjs-hide-fullscreen-toggle--inlineonly');
    }

    if (
      (
        [true, 'inlineonly'] as VideoJsPlayerOptions['hideFullscreenToggle'][]
      ).includes(this.vjsPlayer.options_.hideFullscreenToggle)
    ) {
      this.vjsPlayer.addClass('vjs-hide-fullscreen-toggle');

      const el = this.vjsPlayer.el();

      const handleDblClickCapture = (evt: Event) => {
        evt.preventDefault();
        evt.stopImmediatePropagation();
      };

      el.addEventListener('dblclick', handleDblClickCapture, { capture: true });

      this.vjsPlayer.on(Event.DISPOSE, () => {
        el.removeEventListener('dblclick', handleDblClickCapture);
      });
    }
  }

  dispose() {
    this.vjsPlayer.off(Event.PLAY, this.handleMouseLeave);
    this.vjsPlayer.off(Event.ENDED, this.handleEnded);
    this.vjsPlayer.off(Event.FULLSCREENCHANGE, this.handleFullscreenChange);
    this.vjsPlayer.off(
      Event.CONTENT_RESUME_REQUESTED,
      this.handleContentResumeRequested
    );
    this.vjsPlayer.off(Event.PLAYERRESIZE, this.handlePlayerResize);
    super.dispose();
  }
}

videojs.registerPlugin(PLUGIN_NAME, UIManagerPlugin);

export type UIManagerPluginFactoryFn = (options?: Options) => UIManagerPlugin;

export default UIManagerPlugin;
